import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import toaster, { Theme } from '../../../../shared/toaster';
import { RootState } from '../../../../store/root-reducer';
import {
  BulkLeadsActionType,
  BulkAddToProspectSavedDto,
  BulkAddToSequenceSavedDto,
  BulkLeadExportSavedDto,
  LeadFinderSecondaryTab,
  SavedTabFilters,
} from '../../types';
import {
  bulkAddToProspectLeadAPI,
  bulkAddToSequenceLeadAPI,
  bulkSavedAddToProspectLeadAPI,
  bulkSavedAddToSequenceLeadAPI,
  bulkExportLeadAPI,
  exportAllLeadsAPI,
} from '../../helpers/leads.api';
import { leadFinderBulkOperationLimit } from '../../../../shared/utils/user-details';
import { enableBulkActionRequestPolling } from '../../../home/home-slice';
import { dateToString } from '../../../../shared/functions/date';
import { SAVED_TAB_BULK_OPERATION_LIMIT } from '../../helpers/helper';

interface UseV2LeadBulkActionsProps {
  selectedLeads: number[];
  setSelectedLeads: React.Dispatch<React.SetStateAction<number[]>>;
  isSelectedAllLeads: boolean;
  setIsSelectedAllLeads: React.Dispatch<React.SetStateAction<boolean>>;
  deSelectedLeads: number[];
  setDeSelectedLeads: React.Dispatch<React.SetStateAction<number[]>>;
  activeSubTab: LeadFinderSecondaryTab;
  savedCount?: number;
  handleGeneratePayload: () => any;
  searchLeads: () => void;
  setShowAddToSequenceModal?: React.Dispatch<React.SetStateAction<boolean>>;
  // Tag button props (optional, only needed for saved tab)
  leadTags?: any[];
  savedFilters?: SavedTabFilters;
  setSavedFilters?: React.Dispatch<React.SetStateAction<SavedTabFilters>>;
  handleQuery?: (args: {
    filterKey: string;
    filterValue: any;
    filterType: string;
  }) => void;
  leadsData?: {
    profiles: any[];
    pagination: any;
  };
  fetchLeadTags: () => void;
}

export const useV2LeadBulkActions = ({
  selectedLeads,
  setSelectedLeads,
  isSelectedAllLeads,
  setIsSelectedAllLeads,
  deSelectedLeads,
  setDeSelectedLeads,
  activeSubTab,
  savedCount,
  handleGeneratePayload,
  searchLeads,
  setShowAddToSequenceModal,
  leadTags,
  savedFilters,
  setSavedFilters,
  handleQuery,
  leadsData,
  fetchLeadTags,
}: UseV2LeadBulkActionsProps) => {
  const dispatch = useDispatch();
  // User-applied cap on how many records a select-all bulk action processes.
  const peopleMaxLeadsLimit = useSelector(
    (state: RootState) => state.leadFinderV2.selection.peopleMaxLeadsLimit,
  );
  const [loadingBulkAction, setLoadingBulkAction] = useState({
    loadingAddToProspect: false,
    loadingAddToSequence: false,
    loadingExport: false,
  });
  const bulkTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const selectedLeadObjectsByIdRef = useRef<Map<number, any>>(new Map());

  const handleBulkAddToProspect = useCallback(async () => {
    try {
      setLoadingBulkAction((prev) => ({
        ...prev,
        loadingAddToProspect: true,
      }));

      let res;
      if (isSelectedAllLeads) {
        const savedTabFilters = handleGeneratePayload();
        const tags = savedTabFilters.tags
          ?.split(',')
          .map((tag) => Number(tag.trim()));
        const createdDate = savedTabFilters.createdDate
          ?.split(',')
          .map((date) =>
            dateToString(new Date(decodeURIComponent(date.trim()))),
          );
        const search = Array.isArray(savedTabFilters?.search)
          ? savedTabFilters?.search?.[0]
          : savedTabFilters?.search;
        const listIdsFilterProspect: number[] = [];
        if (savedFilters?.listIds?.includes?.length > 0) {
          listIdsFilterProspect.push(...savedFilters.listIds.includes);
        }
        const payload: BulkAddToProspectSavedDto = {
          deSelectedLeadIds: deSelectedLeads,
          ...(peopleMaxLeadsLimit != null && {
            maxLeadsToBeProcessed: peopleMaxLeadsLimit,
          }),
          leadsFilter: {
            tags,
            search,
            createdDate,
            ...(savedFilters?.listIds?.includes?.length > 0 && {
              listIds: { includes: savedFilters.listIds.includes },
            }),
            ...(savedFilters?.excludeProspects && {
              excludeProspects: savedFilters.excludeProspects,
            }),
            ...(savedFilters?.emailStatus?.length > 0 && {
              emailStatus: savedFilters.emailStatus,
            }),
            ...(listIdsFilterProspect.length > 0 && {
              listIds: listIdsFilterProspect,
            }),
          },
        };
        res = await bulkSavedAddToProspectLeadAPI(payload);
      } else {
        res = await bulkAddToProspectLeadAPI({
          leadIds: selectedLeads,
        });
      }

      if (res) {
        toaster.success(res?.payload?.message);
        setLoadingBulkAction((prev) => ({
          ...prev,
          loadingAddToProspect: false,
        }));
        // Clear selection
        setIsSelectedAllLeads(false);
        setSelectedLeads([]);
        setDeSelectedLeads([]);
      }

      setTimeout(() => {
        dispatch(enableBulkActionRequestPolling());
      }, 5000);
    } catch (e: any) {
      setLoadingBulkAction((prev) => ({
        ...prev,
        loadingAddToProspect: false,
      }));
      toaster.error(e?.message || 'Error adding leads to prospect');
    }
  }, [
    isSelectedAllLeads,
    deSelectedLeads,
    selectedLeads,
    handleGeneratePayload,
    dispatch,
    setIsSelectedAllLeads,
    setSelectedLeads,
    setDeSelectedLeads,
    peopleMaxLeadsLimit,
  ]);

  const handleBulkAddToSequence = useCallback(
    async (sequenceId: number, stepId: number) => {
      try {
        setLoadingBulkAction((prev) => ({
          ...prev,
          loadingAddToSequence: true,
        }));

        let res;
        if (isSelectedAllLeads) {
          const savedTabFilters = handleGeneratePayload();
          const tags = savedTabFilters.tags
            ?.split(',')
            .map((tag) => Number(tag.trim()));
          const createdDate = savedTabFilters.createdDate
            ?.split(',')
            .map((date) =>
              dateToString(new Date(decodeURIComponent(date.trim()))),
            );
          const search = Array.isArray(savedTabFilters?.search)
            ? savedTabFilters?.search?.[0]
            : savedTabFilters?.search;
          const listIdsFilterSeq: number[] = [];
          if (savedFilters?.listIds?.includes?.length > 0) {
            listIdsFilterSeq.push(...savedFilters.listIds.includes);
          }
          const payload: BulkAddToSequenceSavedDto = {
            sequenceId,
            stepId,
            deSelectedLeadIds: deSelectedLeads,
            ...(peopleMaxLeadsLimit != null && {
              maxLeadsToBeProcessed: peopleMaxLeadsLimit,
            }),
            leadsFilter: {
              tags,
              search,
              createdDate,
              ...(savedFilters?.listIds?.includes?.length > 0 && {
                listIds: { includes: savedFilters.listIds.includes },
              }),
              ...(savedFilters?.excludeProspects && {
                excludeProspects: savedFilters.excludeProspects,
              }),
              ...(savedFilters?.emailStatus?.length > 0 && {
                emailStatus: savedFilters.emailStatus,
              }),
              ...(listIdsFilterSeq.length > 0 && {
                listIds: listIdsFilterSeq,
              }),
            },
          };
          res = await bulkSavedAddToSequenceLeadAPI(payload);
        } else {
          res = await bulkAddToSequenceLeadAPI({
            leadIds: selectedLeads,
            sequenceId,
            stepId,
          });
        }

        if (res) {
          toaster.success(res?.payload?.message);
          setLoadingBulkAction((prev) => ({
            ...prev,
            loadingAddToSequence: false,
          }));
          if (setShowAddToSequenceModal) {
            setShowAddToSequenceModal(false);
          }
          // Clear selection
          setIsSelectedAllLeads(false);
          setSelectedLeads([]);
          setDeSelectedLeads([]);
        }

        setTimeout(() => {
          dispatch(enableBulkActionRequestPolling());
        }, 5000);
      } catch (e: any) {
        if (setShowAddToSequenceModal) {
          setShowAddToSequenceModal(false);
        }
        setLoadingBulkAction((prev) => ({
          ...prev,
          loadingAddToSequence: false,
        }));
        toaster.error(e?.message || 'Error adding leads to sequence');
      }
    },
    [
      isSelectedAllLeads,
      deSelectedLeads,
      selectedLeads,
      handleGeneratePayload,
      dispatch,
      setIsSelectedAllLeads,
      setSelectedLeads,
      setDeSelectedLeads,
      setShowAddToSequenceModal,
      peopleMaxLeadsLimit,
    ],
  );

  const handleBulkExport = useCallback(async () => {
    try {
      const isExportAll =
        isSelectedAllLeads || (selectedLeads?.length ?? 0) === 0;
      if (
        activeSubTab === LeadFinderSecondaryTab.SAVED &&
        isExportAll &&
        leadsData.profiles?.length === 0
      ) {
        toaster.error(
          'No leads found. Try adjusting your filters and export again.',
          { theme: Theme.New },
        );
        return;
      }

      setLoadingBulkAction((prev) => ({ ...prev, loadingExport: true }));

      let result;
      if (isSelectedAllLeads || selectedLeads.length === 0) {
        const savedTabFilters = handleGeneratePayload();
        const tags = savedTabFilters.tags
          ?.split(',')
          .map((tag) => Number(tag.trim()));
        const createdDate = savedTabFilters.createdDate
          ?.split(',')
          .map((date) =>
            dateToString(new Date(decodeURIComponent(date.trim()))),
          );
        const search = Array.isArray(savedTabFilters?.search)
          ? savedTabFilters?.search?.[0]
          : savedTabFilters?.search;

        let listIdsFilter: number[] = [];
        if (savedFilters?.listIds?.includes?.length > 0) {
          listIdsFilter = savedFilters.listIds.includes;
        }

        const leadsFilter: any = {
          ...(tags && { tags }),
          ...(search && { search }),
          ...(createdDate && { createdDate }),
          ...(savedFilters?.excludeProspects && {
            excludeProspects: savedFilters.excludeProspects,
          }),
          ...(savedFilters?.emailStatus?.length > 0 && {
            emailStatus: savedFilters.emailStatus,
          }),
        };
        if (listIdsFilter.length > 0) {
          leadsFilter.listIds = listIdsFilter;
        }

        const payload: BulkLeadExportSavedDto = {
          deSelectedLeadIds: deSelectedLeads,
          ...(peopleMaxLeadsLimit != null && {
            maxLeadsToBeProcessed: peopleMaxLeadsLimit,
          }),
          leadsFilter,
        };

        result = await exportAllLeadsAPI(payload);
      } else if (selectedLeads.length > 0) {
        result = await bulkExportLeadAPI({ leadIds: selectedLeads });
      } else {
        toaster.error('No lead selected for export', { theme: Theme.New });
        setLoadingBulkAction((prev) => ({ ...prev, loadingExport: false }));
        return;
      }

      const { message, status } = result.payload;
      if (status === 0) {
        toaster.error(message, { theme: Theme.New });
      } else if (status === 2) {
        toaster.warning(message, { theme: Theme.New });
      } else {
        toaster.success(message, { theme: Theme.New });
      }

      setLoadingBulkAction((prev) => ({ ...prev, loadingExport: false }));
    } catch (err) {
      setLoadingBulkAction((prev) => ({ ...prev, loadingExport: false }));
      toaster.error('Error while exporting lead data');
    }
  }, [
    activeSubTab,
    isSelectedAllLeads,
    savedCount,
    deSelectedLeads,
    selectedLeads,
    handleGeneratePayload,
    leadsData,
    peopleMaxLeadsLimit,
  ]);

  const handleBulkAction = useCallback(
    (actionType: BulkLeadsActionType) => {
      const leadIds = selectedLeads;
      const leadFinderLimit =
        activeSubTab === LeadFinderSecondaryTab.SAVED
          ? SAVED_TAB_BULK_OPERATION_LIMIT
          : leadFinderBulkOperationLimit();

      switch (actionType) {
        case BulkLeadsActionType.ADD_TO_PROSPECT:
          if (leadIds?.length > leadFinderLimit) {
            toaster.error(
              `Please select less than ${leadFinderLimit} leads to proceed!`,
            );
            return;
          }
          handleBulkAddToProspect();
          break;
        case BulkLeadsActionType.ADD_TO_SEQUENCE:
          if (leadIds?.length > leadFinderLimit) {
            toaster.error(
              `Please select less than ${leadFinderLimit} leads to proceed!`,
            );
            return;
          }
          if (setShowAddToSequenceModal) {
            setShowAddToSequenceModal(true);
          }
          break;
        case BulkLeadsActionType.EXPORT:
          if (leadIds?.length > leadFinderLimit) {
            toaster.error(
              `Please select less than ${leadFinderLimit} leads to proceed!`,
            );
            return;
          }
          handleBulkExport();
          break;
        default:
          break;
      }
    },
    [
      selectedLeads,
      activeSubTab,
      handleBulkAddToProspect,
      handleBulkExport,
      setShowAddToSequenceModal,
    ],
  );

  // Handle add tag - refresh leads after tag is added
  const handleAddTag = useCallback(async () => {
    await searchLeads();
    await fetchLeadTags();
    setIsSelectedAllLeads(false);
    setDeSelectedLeads([]);
  }, [searchLeads, fetchLeadTags, setIsSelectedAllLeads, setDeSelectedLeads]);

  // Handle remove tag - refresh leads after tag is removed
  const handleRemoveTag = useCallback(async () => {
    await searchLeads();
    setIsSelectedAllLeads(false);
    setDeSelectedLeads([]);
  }, [searchLeads, setIsSelectedAllLeads, setDeSelectedLeads]);

  useEffect(() => {
    leadsData?.profiles?.forEach((lead) => {
      if (lead?.id != null) {
        selectedLeadObjectsByIdRef.current.set(Number(lead.id), lead);
      }
    });
  }, [leadsData?.profiles]);

  // Get selected lead objects (with tags) for tag autosuggest
  const selectedLeadObjects = useMemo(() => {
    if (!leadsData?.profiles) {
      return [];
    }

    if (isSelectedAllLeads) {
      return leadsData.profiles;
    }

    if (selectedLeads.length === 0) {
      return [];
    }

    return selectedLeads
      .map((leadId) => selectedLeadObjectsByIdRef.current.get(Number(leadId)))
      .filter(Boolean);
  }, [isSelectedAllLeads, leadsData?.profiles, selectedLeads]);

  // Prepare tag button props for saved tab - only when on saved tab
  // setSavedFilters, setSelectedLeads, and handleQuery are stable references
  const tagButtonProps = useMemo(() => {
    if (activeSubTab !== LeadFinderSecondaryTab.SAVED) {
      return undefined;
    }
    if (!leadTags || !savedFilters || !setSavedFilters || !handleQuery) {
      return undefined;
    }
    return {
      handleAddTag,
      handleRemoveTag,
      leadTags,
      selectedLeads,
      selectedLeadObjects,
      deselectedLeads: deSelectedLeads,
      leadsFilter: savedFilters,
      setSavedFilters,
      handleQuery,
      setSelectedLeads,
    };
  }, [
    activeSubTab,
    handleAddTag,
    handleRemoveTag,
    leadTags,
    selectedLeads,
    selectedLeadObjects,
    deSelectedLeads,
    savedFilters,
    // setSavedFilters, setSelectedLeads, handleQuery are stable - no need in deps
  ]);

  // Cleanup timeout on unmount
  useEffect(
    () => () => {
      if (bulkTimeoutRef.current) {
        clearTimeout(bulkTimeoutRef.current);
      }
    },
    [],
  );

  return {
    loadingBulkAction,
    handleBulkAddToProspect,
    handleBulkAddToSequence,
    handleBulkExport,
    handleBulkAction,
    tagButtonProps,
  };
};
